import API from '@Services/apiAxios';

export const DOCUMENT_TYPE = 'shipment'; // Flexible configuration for changing it later in one place

export interface InvalidFieldItem {
  field?: string;
  reason?: string;
  [key: string]: any;
}

export interface ExtractedProduct {
  description?: string;
  quantity?: number | string | null;
  unitValue?: number | string | null;
  HSNCode?: string | number;
  HSNName?: string;
  productId?: string;
  productIdType?: string;
  merchantProductId?: string;
  standardManufacturerProductId?: string;
  nonStandardManufacturerProductId?: string;
  [key: string]: any;
}

export interface ExtractedBox {
  length?: number | string;
  width?: number | string;
  height?: number | string;
  weight?: number | string;
  boxCopyCount?: number;
  products?: ExtractedProduct[];
  [key: string]: any;
}

export interface PackageDetails {
  invoiceNumber?: string;
  currency?: string;
  unitOfMeasurement?: string;
  packaging?: boolean;
  insurance?: boolean;
  isDocument?: boolean;
  isCod?: boolean;
  isDropOff?: boolean;
  codAmount?: number | string;
  box?: ExtractedBox[];
  boxes?: ExtractedBox[];
  shipmentMode?: string;
  incoterm?: string;
  freight?: number | string;
  freightInsurance?: number | string;
  iossNumber?: string;
  recipientVatTaxId?: string;
  ior?: string;
  iorEoriNumber?: string;
  [key: string]: any;
}

export type ExtractedPackageDetails = PackageDetails;

export interface ExtractedShipperConsignee {
  name?: string;
  companyName?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  phone?: {
    number?: string;
    telephoneCode?: string;
  };
  phoneNumber?: string;
  telephoneCode?: string;
  email?: string;
  [key: string]: any;
}

export interface ExtractedShipmentFields {
  shipper?: ExtractedShipperConsignee;
  consignee?: ExtractedShipperConsignee;
  package?: PackageDetails;
  invoiceNumber?: string;
  userReferenceNumber?: string;
  shipmentMode?: string;
  incoterm?: string;
  freight?: number | string;
  freightInsurance?: number | string;
  iossNumber?: string;
  recipientVatTaxId?: string;
  ior?: string;
  iorEoriNumber?: string;
  box?: ExtractedBox[];
  boxes?: ExtractedBox[];
  [key: string]: any;
}

export type ShipmentFormState = ExtractedShipmentFields;
export type ShipmentFormFields = ExtractedShipmentFields;

export interface ExtractionResponse {
  jobId?: string;
  status?: string;
  documentType?: string;
  confidence?: number;
  invalidFields?: (string | InvalidFieldItem)[];
  error?: string | null;
  fields?: ExtractedShipmentFields;
  message?: string;
  state?: string;
  [key: string]: any;
}

export interface ExtractionPollingOptions {
  onStatusChange?: (status: string) => void;
  isCancelled?: () => boolean;
  registerTimer?: (timer: NodeJS.Timeout | null) => void;
  pollIntervalMs?: number;
}

/**
 * Format invalidFields into a human-readable, actionable message.
 * Extracts `reason` or `field` from objects, handles string items, and eliminates "[object Object]".
 */
export const formatInvalidFieldsReason = (invalidFields?: (string | InvalidFieldItem)[]): string => {
  if (!Array.isArray(invalidFields) || invalidFields.length === 0) return '';

  const messages: string[] = [];

  for (const item of invalidFields) {
    if (!item) continue;
    if (typeof item === 'string') {
      messages.push(item);
    } else if (typeof item === 'object') {
      if (item.reason && typeof item.reason === 'string') {
        messages.push(item.reason);
      } else if (item.field && typeof item.field === 'string') {
        messages.push(`Validation issue on ${item.field}`);
      } else {
        const str = JSON.stringify(item);
        if (str !== '{}') messages.push(str);
      }
    }
  }

  return messages.join('; ');
};

/**
 * Safely unwrap extraction response object whether returned at root level
 * or wrapped inside response.data.data
 */
export const unwrapExtractionResponse = (raw: any): ExtractionResponse => {
  if (!raw) return {};
  if (raw.jobId || raw.status || raw.fields) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object') {
    if (raw.data.jobId || raw.data.status || raw.data.fields) {
      return {
        ...raw.data,
        error: raw.data.error || raw.error || null,
      };
    }
  }
  return raw;
};

/**
 * Trigger async document extraction with documentType = "shipment" and mode = "async"
 * POST /speedboxbot/extract
 */
export const extractDocument = async (s3Key: string): Promise<ExtractionResponse> => {
  const { data } = await API.post<ExtractionResponse>('/speedboxbot/extract', {
    documentType: DOCUMENT_TYPE,
    s3Key,
    mode: 'async',
  });
  return unwrapExtractionResponse(data);
};

/**
 * Trigger async text extraction with documentType = "shipment" and mode = "async"
 * POST /speedboxbot/extract
 */
export const extractTextDetails = async (textContent: string): Promise<ExtractionResponse> => {
  const { data } = await API.post<ExtractionResponse>('/speedboxbot/extract', {
    documentType: DOCUMENT_TYPE,
    textContent,
    mode: 'async',
  });
  return unwrapExtractionResponse(data);
};

/**
 * Poll single job status
 * GET /speedboxbot/extract/{jobId}
 */
export const getExtractionStatus = async (jobId: string): Promise<ExtractionResponse> => {
  const { data } = await API.get<ExtractionResponse>(`/speedboxbot/extract/${jobId}`);
  return unwrapExtractionResponse(data);
};

/**
 * Poll extraction job until terminal status (completed, needs_review, failed)
 * Waits 10 seconds before initial poll and between consecutive polls.
 */
export const pollExtraction = async (
  initialResponse: ExtractionResponse,
  options: ExtractionPollingOptions = {},
): Promise<ExtractionResponse> => {
  const { onStatusChange, isCancelled, registerTimer, pollIntervalMs = 10000 } = options;

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // If already terminal (e.g. sync fallback)
  const initialStatus = initialResponse.status;
  if (initialStatus === 'completed' || initialStatus === 'failed' || initialStatus === 'needs_review') {
    return initialResponse;
  }

  const jobId = initialResponse.jobId;
  if (!jobId) {
    if (initialResponse.fields && Object.keys(initialResponse.fields).length > 0) {
      return initialResponse;
    }
    return {
      status: 'failed',
      error: initialResponse.error || 'Failed to initialize extraction: missing jobId in response',
    };
  }

  let currentStatus = initialStatus || 'pending';

  while (currentStatus === 'pending' || currentStatus === 'processing') {
    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.(
      currentStatus === 'processing'
        ? 'AI is analyzing shipment details... (processing)'
        : 'AI extraction in progress... (waiting for results)',
    );

    // Wait 10 seconds before the poll without overlapping requests
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        registerTimer?.(null);
        if (isCancelled?.()) {
          reject(new Error('Extraction cancelled'));
        } else {
          resolve();
        }
      }, pollIntervalMs);
      registerTimer?.(timer);
    });

    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.('Checking extraction status...');
    const pollResult = await getExtractionStatus(jobId);

    currentStatus = pollResult.status || 'failed';

    if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'needs_review') {
      return pollResult;
    }
  }

  return {
    status: currentStatus,
    error: `Unexpected extraction status: ${currentStatus}`,
  };
};

export interface StartBatchExtractionResponse {
  batchId: string;
  [key: string]: any;
}

export interface BatchExtractionStatusResponse {
  batchId: string;
  status: string;
  progress?: number;
  result?: any;
  message?: string;
  [key: string]: any;
}

export const startBatchExtraction = async (batchId: string, files: string[]): Promise<StartBatchExtractionResponse> => {
  const { data } = await API.post<StartBatchExtractionResponse>('/speedboxbot/extract/batch', {
    batchId,
    documentType: DOCUMENT_TYPE,
    files,
  });
  return data;
};

export const getBatchExtractionStatus = async (batchId: string): Promise<BatchExtractionStatusResponse> => {
  const { data } = await API.get<BatchExtractionStatusResponse>(`/speedboxbot/extract/batch/${batchId}`);
  return data;
};

export const pollBatchStatus = (
  batchId: string,
  onProgress: (status: BatchExtractionStatusResponse) => void,
  cancelRef: { cancelled: boolean },
  intervalMs = 3000,
): Promise<BatchExtractionStatusResponse> => {
  return new Promise((resolve, reject) => {
    const run = async () => {
      if (cancelRef.cancelled) {
        reject(new Error('Batch extraction polling cancelled.'));
        return;
      }
      try {
        const response = await getBatchExtractionStatus(batchId);
        if (cancelRef.cancelled) {
          reject(new Error('Batch extraction polling cancelled.'));
          return;
        }
        onProgress(response);

        const status = response.status?.toLowerCase();
        if (status === 'completed' || status === 'success') {
          resolve(response);
        } else if (status === 'failed' || status === 'error') {
          reject(new Error(response.message || 'Batch extraction failed.'));
        } else {
          setTimeout(run, intervalMs);
        }
      } catch (error) {
        reject(error);
      }
    };
    run();
  });
};
